import { Box, Button, Stack, Typography, Link } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/authContext.js";
import { getContentTypeSlug } from "../../utils/utils.js";

export default function PostImport({ importData, sourceConfig }) {
  const { status, summary } = importData || {};
  const navigate = useNavigate();
  const { user } = useAuth();

  const sections = summary?.sections || [];
  const skippedSections = summary?.skipped_sections || [];

  const hasSkipped = skippedSections.some(
    (section) => section.rows && section.rows.length > 0,
  );

  // jsPDF and its autotable plugin are ~360KB and only ever needed once the user
  // asks for the file, so they are fetched on click rather than shipped in the
  // bundle every visitor downloads.
  const handleDownloadTable = async () => {
    if (!hasSkipped) return;

    let jsPDF, autoTable;
    try {
      // A deploy renames the built files, so a tab opened before it can ask for
      // a name that no longer exists. Only a page refresh picks up the new names,
      // which is why the message says to refresh rather than retrying here.
      [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
        import("jspdf"),
        import("jspdf-autotable"),
      ]);
    } catch (error) {
      Sentry.captureException(error, { tags: { chunk_load: true } });
      toast.error(
        "Couldn't prepare the download. Please refresh the page and try again.",
      );
      return;
    }

    const doc = new jsPDF();

    doc.setFontSize(16);
    doc.setFont(undefined, "bold");
    doc.text("Items requiring manual import to Curate", 14, 20);

    doc.setFontSize(11);
    doc.setFont(undefined, "normal");

    // Intro paragraph
    const introText =
      "Thanks for importing to Curate! Apologies not everything could be brought across automatically. You'll find a list of items that couldn't be imported below; feel free to add these manually. If there's too many, just reach out via the feedback form and we can help with a bulk upload.";

    const splitIntro = doc.splitTextToSize(introText, 180);
    doc.text(splitIntro, 14, 26);

    // Set Y dynamically based on paragraph height
    let currentY = 26 + splitIntro.length * 6 + 4;

    skippedSections.forEach((section) => {
      if (!section.rows || section.rows.length === 0) return;

      // Section Title
      doc.setFont(undefined, "bold");
      doc.text(section.label, 14, currentY);
      currentY += 6;

      if (section.help_text) {
        doc.setFont(undefined, "normal");
        doc.text(section.help_text, 14, currentY, { maxWidth: 180 });
        currentY += 6;
      }

      autoTable(doc, {
        startY: currentY,
        head: [section.columns.map((col) => col.label)],
        body: section.rows.map((row) =>
          section.columns.map((col) =>
            row[col.key] != null ? String(row[col.key]) : "",
          ),
        ),
        styles: { fontSize: 10, cellPadding: 2 },
        headStyles: {
          fillColor: "#b1a7ec",
          textColor: "#fff",
          halign: "left",
        },
        theme: "striped",
      });

      currentY = doc.lastAutoTable.finalY + 10;
    });

    doc.save("curate_items_requiring_manual_import.pdf");
  };

  // Deep-link to the content type that was just imported (e.g. books for
  // Goodreads) rather than the user's saved preference, so they land on the
  // items they just imported instead of a possibly-empty tab.
  const handleViewImported = () => {
    const contentSlug =
      getContentTypeSlug(sourceConfig?.contentType?.[0]) ?? "movies";
    navigate(`/track/u/${user.username}/${contentSlug}/finished`);
  };

  if (importData.status === "failed") {
    return (
      <Typography>
        Something went wrong during the import. Please{" "}
        <Link
          href="https://app.youform.com/forms/fghdoj5a"
          target="_blank"
          rel="noopener noreferrer"
        >
          contact us
        </Link>{" "}
        and we'll help sort it out for you
      </Typography>
    );
  }

  return (
    <Box>
      {status === "completed_with_errors" ? (
        <Typography variant="body1" gutterBottom>
          Thanks for importing to Curate! Some parts of your import were
          successful, but we ran into an unexpected issue while processing the
          rest. We’re here to help - just{" "}
          <Link
            href="https://app.youform.com/forms/fghdoj5a"
            target="_blank"
            rel="noopener noreferrer"
          >
            contact us
          </Link>{" "}
          and we’ll sort it out for you. Here's a breakdown of what was
          processed:
        </Typography>
      ) : (
        <Typography variant="body1" gutterBottom>
          Thanks for importing to Curate! Here's how it went:
        </Typography>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 1,
          my: 2,
          mx: 1,
        }}
      >
        {sections.map((section) => (
          <Stack direction="column" spacing={0} px={1} key={section.key}>
            <Typography variant="h5" fontWeight="bold" align="center">
              {section.created ?? "-"} / {section.total ?? "-"}
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              {section.label}
            </Typography>
          </Stack>
        ))}
      </Box>

      {sourceConfig?.postImportNotes?.length > 0 && (
        <>
          <Typography sx={{ mt: 2 }}>Please note:</Typography>
          <Box component="ul" sx={{ pl: 3, mt: 1 }}>
            {sourceConfig.postImportNotes.map((note, index) => (
              <Typography component="li" gutterBottom key={index}>
                {note}
              </Typography>
            ))}
          </Box>
        </>
      )}

      {hasSkipped && (
        <Typography variant="body1" sx={{ mt: 3, mb: 1 }}>
          A PDF of the items that failed to import is available for download
          below for your reference. You can add these manually or{" "}
          <Link
            href="https://app.youform.com/forms/fghdoj5a"
            target="_blank"
            rel="noopener noreferrer"
          >
            contact us
          </Link>{" "}
          if you need help
        </Typography>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: hasSkipped ? "space-between" : "flex-end",
          alignItems: "center",
          gap: 1,
          mt: 2,
        }}
      >
        {hasSkipped && (
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTable}
          >
            Failed import items
          </Button>
        )}
        <Button variant="contained" onClick={handleViewImported}>
          View imported items
        </Button>
      </Box>
    </Box>
  );
}
